/**
 * `reveal_gate` renderer — a pre-auth gate showing blurred character
 * portraits with an unlock CTA and optional login link. Used by the
 * SpicyChat onboarding funnel (step 8, NX-9560).
 *
 * Step JSON shape:
 *   {
 *     "type": "reveal_gate",
 *     "data": {
 *       "eyebrow": "YOUR MATCHES ARE READY",
 *       "headline": "We found your vibe",
 *       "description": "Characters built around your mood, style, and opening scene.",
 *       "tiles": [
 *         { "imageUrl": "…", "locked": false, "badge": "Match" },
 *         { "imageUrl": "…", "locked": true }
 *       ],
 *       "chips": ["Flirty tone", "Anime style"],
 *       "ctaLabel": "Create free account to unlock",
 *       "loginPrompt": "Already have one?",
 *       "loginLabel": "Log in"
 *     }
 *   }
 */
import { RevealGate } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

const tileSchema = z.object({
  imageUrl: z.string().min(1),
  label: z.string().optional(),
  locked: z.boolean().optional(),
  badge: z.string().optional(),
});

export const revealGateSchema = z
  .object({
    eyebrow: z.string().optional(),
    headline: z.string().min(1),
    description: z.string().optional(),
    tiles: z.array(tileSchema).min(1),
    chips: z.array(z.string().min(1)).optional(),
    ctaLabel: z.string().optional(),
    loginPrompt: z.string().optional(),
    loginLabel: z.string().optional(),
    revealDelayMs: z.number().int().min(0).optional(),
    revealDurationMs: z.number().int().min(0).optional(),
    staggerMs: z.number().int().min(0).optional(),
  })
  .strict();

export type RevealGateData = z.infer<typeof revealGateSchema>;

const RevealGateRenderer = ({ data, step, onAnswer, onAdvance }: RendererProps<RevealGateData>) => {
  const handleCta = (): void => {
    onAnswer("reveal_gate_action", { value: "create_account" });
    onAdvance();
  };

  const handleLogin = (): void => {
    onAnswer("reveal_gate_action", { value: "login" });
    onAdvance();
  };

  return (
    <RevealGate
      headline={data.headline}
      description={data.description}
      eyebrow={data.eyebrow}
      tiles={data.tiles}
      chips={data.chips}
      ctaLabel={data.ctaLabel}
      loginPrompt={data.loginPrompt}
      loginLabel={data.loginLabel}
      onCta={handleCta}
      onLogin={data.loginLabel || data.loginPrompt ? handleLogin : undefined}
      revealDelayMs={data.revealDelayMs}
      revealDurationMs={data.revealDurationMs}
      staggerMs={data.staggerMs}
      testId={`renderer-reveal-gate-${step.id}`}
    />
  );
};

export const revealGateRenderer = createRenderer({
  type: "reveal_gate",
  dataSchema: revealGateSchema,
  label: "Reveal gate",
  Component: RevealGateRenderer,
});

export default revealGateRenderer;
